import { Component, type ErrorInfo, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle, RotateCcw } from "lucide-react";

/**
 * Sayfa içeriği render sırasında hata atarsa TÜM uygulama (kenar çubuğu dahil)
 * beyaz ekrana gitmesin: yalnız içerik alanı bir hata kartına döner. Rota
 * değişince sıfırlanır (`key = pathname`). Tipik tetikleyiciler: beklenmeyen
 * API verisi, tarayıcı çevirisi/eklentilerinin DOM'u değiştirmesi
 * (React `insertBefore`/`removeChild` NotFoundError).
 */
class Boundary extends Component<{ children: ReactNode; fallback: (error: Error) => ReactNode }, { error: Error | null }> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[RouteErrorBoundary]", error, info.componentStack);
  }

  render() {
    return this.state.error ? this.props.fallback(this.state.error) : this.props.children;
  }
}

function Fallback({ error }: { error: Error }) {
  const { t } = useTranslation();
  return (
    <div role="alert" className="mx-auto max-w-lg rounded-2xl border border-red-500/30 bg-red-500/5 px-6 py-8 text-center space-y-4">
      <AlertTriangle className="mx-auto h-8 w-8 text-red-500" aria-hidden="true" />
      <div>
        <h2 className="text-lg font-semibold text-foreground">{t("errorBoundary.title")}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{t("errorBoundary.desc")}</p>
      </div>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors cursor-pointer"
      >
        <RotateCcw className="h-4 w-4" aria-hidden="true" />
        <span>{t("errorBoundary.reload")}</span>
      </button>
      <details className="text-left text-xs text-muted-foreground">
        <summary className="cursor-pointer">{t("errorBoundary.details")}</summary>
        <pre className="mt-2 whitespace-pre-wrap break-words rounded-lg bg-muted/50 p-3">{error.name}: {error.message}</pre>
      </details>
    </div>
  );
}

export function RouteErrorBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return (
    <Boundary key={pathname} fallback={(error) => <Fallback error={error} />}>
      {children}
    </Boundary>
  );
}
